import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Loader2, AlertCircle, Pencil } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const maskId = (id) => (id && id.length > 4 ? `${id.slice(0, 4)}••••••` : id);

/** Step 5 of 5 — Review & Submit. Uploads the ID doc + selfie, then creates the
 *  professional profile with everything collected across steps 2-4. */
const StepReview = ({ data, user, onEditStep }) => {
  const navigate = useNavigate();
  const [catalog, setCatalog] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    axios.get(`${API}/services/popular`).then((res) => setCatalog(res.data)).catch(() => {});
  }, []);

  const getName = (slug) => catalog.find((s) => s.slug === slug)?.name || slug;

  const uploadDoc = async (file, docType) => {
    const fd = new FormData();
    fd.append('doc_type', docType);
    fd.append('file', file);
    const res = await axios.post(`${API}/professionals/upload-verification-doc`, fd, { withCredentials: true });
    return res.data.path;
  };

  const handleSubmit = async () => {
    setError('');
    setSubmitting(true);
    try {
      setProgress('Uploading ID document…');
      const idDocumentPath = await uploadDoc(data.idDocumentFile, 'id_document');
      setProgress('Uploading selfie…');
      const selfiePath = await uploadDoc(data.selfieFile, 'selfie');

      setProgress('Submitting application…');
      await axios.post(
        `${API}/professionals/register`,
        {
          name: data.name,
          phone: data.phone,
          services: data.services,
          service_areas: `${data.city}, ${data.province}`,
          province: data.province,
          city: data.city,
          service_radius_km: data.serviceRadiusKm,
          id_number: data.idNumber,
          id_document_path: idDocumentPath,
          selfie_path: selfiePath,
          bank_name: data.bankName,
          bank_account_number: data.bankAccountNumber,
          bank_account_holder: data.bankAccountHolder,
        },
        { withCredentials: true }
      );
      setSuccess(true);
    } catch (e) {
      setError(e.response?.data?.detail?.message || e.response?.data?.detail || 'Could not submit your application. Please try again.');
    } finally {
      setSubmitting(false);
      setProgress('');
    }
  };

  if (success) {
    return (
      <div className="text-center py-10" data-testid="registration-success">
        <CheckCircle size={64} className="text-green-500 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Application Submitted!</h2>
        <p className="text-sm text-gray-600 max-w-sm mx-auto mb-6">
          Your identity verification is pending review (usually within 24-48 hours). You can start
          setting up your profile now — lead unlocking will switch on automatically once you're approved.
        </p>
        <button
          onClick={() => navigate('/')}
          className="bg-[#37b34a] hover:bg-[#2d9a3e] text-white font-semibold px-6 py-2.5 rounded-lg"
        >
          Go to Homepage
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="step-review">
      {error && (
        <div data-testid="review-error" className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-lg flex items-start gap-2">
          <AlertCircle size={16} className="flex-shrink-0 mt-0.5" /> {error}
        </div>
      )}

      <ReviewCard title="Account" onEdit={() => onEditStep(0)} editable={!user}>
        <p className="text-sm text-gray-700">{user?.name} ({user?.email})</p>
      </ReviewCard>

      <ReviewCard title="Identity" onEdit={() => onEditStep(1)}>
        <p className="text-sm text-gray-700">{data.name} · {data.phone}</p>
        <p className="text-sm text-gray-500">ID: {maskId(data.idNumber)}</p>
        <div className="flex gap-2 mt-1 text-xs text-[#37b34a] font-medium">
          <span>{data.idDocumentFile ? '✓ ID document uploaded' : '✗ ID document missing'}</span>
          <span>·</span>
          <span>{data.selfieFile ? '✓ Selfie uploaded' : '✗ Selfie missing'}</span>
        </div>
      </ReviewCard>

      <ReviewCard title="Area" onEdit={() => onEditStep(2)}>
        <p className="text-sm text-gray-700">{data.city}, {data.province} · {data.serviceRadiusKm}km radius</p>
      </ReviewCard>

      <ReviewCard title="Services" onEdit={() => onEditStep(3)}>
        <div className="flex flex-wrap gap-1.5">
          {data.services.map((s) => (
            <span key={s.service_id} className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-full font-medium">
              {getName(s.service_id)} · {s.experience_level}
            </span>
          ))}
        </div>
      </ReviewCard>

      <ReviewCard title="Bank Details" onEdit={() => onEditStep(4)}>
        {data.bankName || data.bankAccountNumber || data.bankAccountHolder ? (
          <p className="text-sm text-gray-700">{data.bankName} · {data.bankAccountHolder} · {maskId(data.bankAccountNumber)}</p>
        ) : (
          <p className="text-sm text-gray-400 italic">Skipped — you can add this anytime from your profile.</p>
        )}
      </ReviewCard>

      <button
        type="button" onClick={handleSubmit} disabled={submitting}
        data-testid="submit-application-btn"
        className="w-full bg-[#37b34a] hover:bg-[#2d9a3e] disabled:bg-gray-300 text-white font-semibold px-6 py-3 rounded-lg transition-colors flex items-center justify-center gap-2"
      >
        {submitting ? <><Loader2 size={18} className="animate-spin" /> {progress || 'Submitting…'}</> : 'Submit Application'}
      </button>
    </div>
  );
};

const ReviewCard = ({ title, onEdit, children }) => (
  <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
    <div className="flex items-center justify-between mb-1.5">
      <h3 className="text-xs font-bold uppercase tracking-wide text-gray-500">{title}</h3>
      <button type="button" onClick={onEdit} className="text-xs text-[#37b34a] hover:text-[#2d9a3e] font-semibold flex items-center gap-1">
        <Pencil size={11} /> Edit
      </button>
    </div>
    {children}
  </div>
);

export default StepReview;
